import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import UserSearchInput from './UserSearchInput';
import MessageComposer from './MessageComposer';
import UserAvatar from './UserAvatar';
import './ReplyModal.css';

function ReplyModal({ show, onHide, onSend, originalMessage, thread, replyType }) {
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (show && originalMessage && thread) {
      // Set default recipients based on reply type
      if (replyType === 'sender') {
        // Reply to sender only
        const sender = thread.participantDetails.find(
          p => p.userId === originalMessage.senderId
        );
        if (sender) {
          setSelectedUsers([sender]);
        }
      } else if (replyType === 'all') {
        // Reply to all participants
        setSelectedUsers([...thread.participantDetails]);
      } else {
        // Custom - start with empty selection
        setSelectedUsers([]);
      }
    }
  }, [show, originalMessage, thread, replyType]);

  const handleUserSelect = (user) => {
    if (!selectedUsers.find(u => u.userId === user.userId)) {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleUserRemove = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.userId !== userId));
  };

  const handleSendReply = async (content) => {
    if (selectedUsers.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    if (!content.trim()) {
      setError('Please enter a message');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const recipientIds = selectedUsers.map(u => u.userId);
      await onSend({
        threadId: thread.threadId,
        recipients: recipientIds,
        content: content.trim()
      });

      // Reset form
      setSelectedUsers([]);
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to send reply');
      throw err;
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setSelectedUsers([]);
      setError(null);
      onHide();
    }
  };

  const getModalTitle = () => {
    if (replyType === 'sender') {
      return 'Reply to Sender';
    } else if (replyType === 'all') {
      return 'Reply to All';
    } else {
      return 'Reply with Custom Recipients';
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" className="reply-modal">
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Original message preview */}
        {originalMessage && (
          <div className="original-message-preview mb-3">
            <div className="d-flex align-items-start gap-2">
              <UserAvatar
                photoUrl={originalMessage.senderPhotoUrl}
                displayName={originalMessage.senderName}
                size="sm"
              />
              <div className="flex-grow-1">
                <div className="fw-bold">{originalMessage.senderName}</div>
                <div className="text-muted small">{originalMessage.content}</div>
              </div>
            </div>
          </div>
        )}

        <Form.Group className="mb-3">
          <Form.Label>To:</Form.Label>

          {/* Selected users chips */}
          {selectedUsers.length > 0 && (
            <div className="selected-users mb-2">
              {selectedUsers.map(user => (
                <div key={user.userId} className="user-chip">
                  <UserAvatar
                    photoUrl={user.photoUrl}
                    displayName={user.displayName}
                    size="sm"
                    className="me-1"
                  />
                  <span>{user.displayName}</span>
                  {replyType === 'custom' && (
                    <button
                      type="button"
                      className="remove-user-btn"
                      onClick={() => handleUserRemove(user.userId)}
                      disabled={sending}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* User search input - only for custom replies */}
          {replyType === 'custom' && (
            <UserSearchInput
              onUserSelect={handleUserSelect}
              excludeUserIds={selectedUsers.map(u => u.userId)}
              disabled={sending}
            />
          )}
        </Form.Group>

        <Form.Group>
          <Form.Label>Message:</Form.Label>
          <MessageComposer
            onSend={handleSendReply}
            disabled={sending || selectedUsers.length === 0}
            placeholder="Type your reply..."
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={sending}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReplyModal;
